import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import api, { formatINR, formatINRExact } from "../lib/api";
import { payWithRazorpay, fetchRazorpayConfig, loadRazorpaySdk } from "../lib/razorpay";
import { useSettings } from "../contexts/SettingsContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { CheckCircle2, Tag, X, Lock } from "lucide-react";
import { toast, Toaster } from "sonner";

// One-time UTM capture: read `?utm_*` (and `?coupon=`) from the URL on first load
// and persist them in sessionStorage so the order picks them up at checkout.
function captureUtmsFromUrl() {
    if (typeof window === "undefined") return null;
    const p = new URLSearchParams(window.location.search);
    const u = {
        utm_source: p.get("utm_source"),
        utm_medium: p.get("utm_medium"),
        utm_campaign: p.get("utm_campaign"),
        utm_content: p.get("utm_content"),
    };
    if (Object.values(u).some(Boolean)) {
        sessionStorage.setItem("hpl_utm", JSON.stringify(u));
    }
    const c = p.get("coupon");
    if (c) sessionStorage.setItem("hpl_coupon", c.trim().toUpperCase());
    return u;
}

export default function Checkout() {
    const { items, subtotal, clear } = useCart();
    const nav = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({ name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "" });
    const [paymentMethod, setPaymentMethod] = useState("razorpay");
    const [gateway, setGateway] = useState({ enabled: false, key_id: "", mode: null });
    const [quote, setQuote] = useState(null);
    const { settings } = useSettings();
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    // Coupon flow
    const [couponInput, setCouponInput] = useState("");
    const [applied, setApplied] = useState(null);  // { code, discount, description }
    const [couponBusy, setCouponBusy] = useState(false);

    useEffect(() => {
        captureUtmsFromUrl();
        const preCoupon = sessionStorage.getItem("hpl_coupon");
        if (preCoupon) setCouponInput(preCoupon);
        fetchRazorpayConfig().then((cfg) => {
            setGateway(cfg);
            if (cfg.enabled) loadRazorpaySdk();
            else setPaymentMethod("cod");
        });
    }, []);

    const shipping = quote ? quote.shipping : (subtotal === 0 ? 0 : subtotal >= 999 ? 0 : 60);
    const discount = quote ? quote.discount : (applied?.discount || 0);
    const prepaidDiscount = quote ? (quote.prepaid_discount || 0) : 0;
    const prepaidPct = quote?.prepaid_discount_pct || Number(settings?.prepaid_discount_pct || 0);
    const total = quote ? quote.total : Math.max(0, subtotal + shipping - discount);

    const applyCoupon = async (codeArg) => {
        const code = (codeArg || couponInput).trim().toUpperCase();
        if (!code) return;
        setCouponBusy(true);
        try {
            const { data } = await api.get("/coupons/validate", { params: { code, subtotal } });
            setApplied(data);
            setCouponInput(code);
            sessionStorage.setItem("hpl_coupon", code);
            toast.success(`${code} applied — you save ${formatINR(data.discount)}`);
        } catch (e) {
            setApplied(null);
            toast.error(e.response?.data?.detail || "Invalid coupon");
        } finally { setCouponBusy(false); }
    };
    const removeCoupon = () => {
        setApplied(null);
        sessionStorage.removeItem("hpl_coupon");
        toast.success("Coupon removed");
    };

    // Authoritative price breakdown — re-quoted whenever the cart, coupon or
    // payment method changes so the prepaid discount is never guessed client-side.
    useEffect(() => {
        if (items.length === 0) { setQuote(null); return; }
        let dead = false;
        api.post("/checkout/quote", {
            items: items.map(i => ({ product_id: i.product_id, size_id: i.size_id, quantity: i.quantity })),
            coupon_code: applied?.code || null,
            payment_method: paymentMethod,
            order_type: "retail",
        })
            .then(({ data }) => { if (!dead) setQuote(data); })
            .catch(() => { if (!dead) setQuote(null); });
        return () => { dead = true; };
    }, [items, applied, paymentMethod]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (items.length === 0) return toast.error("Cart is empty");
        const REQUIRED = [
            ["name", "Full name"],
            ["phone", "Phone"],
            ["line1", "Address line 1"],
            ["city", "City"],
            ["state", "State"],
            ["pincode", "Pincode"],
        ];
        for (const [k, label] of REQUIRED) {
            if (!String(form[k] || "").trim()) {
                return toast.error(`${label} is required`);
            }
        }
        setSubmitting(true);
        try {
            const utm = JSON.parse(sessionStorage.getItem("hpl_utm") || "{}");
            const payload = {
                items: items.map(i => ({ product_id: i.product_id, size_id: i.size_id, quantity: i.quantity })),
                address: form,
                payment_method: paymentMethod,
                coupon_code: applied ? applied.code : null,
                utm_source: utm.utm_source || null,
                utm_medium: utm.utm_medium || null,
                utm_campaign: utm.utm_campaign || null,
                utm_content: utm.utm_content || null,
            };

            if (paymentMethod === "razorpay") {
                // Nothing is written to the ledger until Razorpay confirms the money.
                await payWithRazorpay({ purpose: "retail", order: payload });
                clear();
                sessionStorage.removeItem("hpl_coupon");
                sessionStorage.removeItem("hpl_utm");
                toast.success("Payment successful — order confirmed!");
                nav(`/orders`);
                return;
            }

            await api.post("/orders", payload);
            clear();
            sessionStorage.removeItem("hpl_coupon");
            sessionStorage.removeItem("hpl_utm");
            toast.success("Order placed!");
            nav(`/orders`);
        } catch (err) {
            if (err.cancelled) toast.error(err.message);
            else toast.error(err.response?.data?.detail || err.message || "Order failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="checkout-page">
            <Toaster position="top-center" />
            <h1 className="font-serif text-5xl text-stone-900 mb-10">Checkout</h1>
            <form onSubmit={onSubmit} className="grid lg:grid-cols-3 gap-10">
                <div className="lg:col-span-2 space-y-6 bg-white border border-stone-200 p-8">
                    <div className="label-eyebrow text-sky-700">Shipping address</div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Full name" v={form.name} on={set("name")} testId="field-name" />
                        <Field label="Phone" v={form.phone} on={set("phone")} testId="field-phone" />
                    </div>
                    <Field label="Address line 1" v={form.line1} on={set("line1")} testId="field-line1" />
                    <Field label="Address line 2 (optional)" v={form.line2} on={set("line2")} testId="field-line2" />
                    <div className="grid sm:grid-cols-3 gap-4">
                        <Field label="City" v={form.city} on={set("city")} testId="field-city" />
                        <Field label="State" v={form.state} on={set("state")} testId="field-state" />
                        <Field label="Pincode" v={form.pincode} on={set("pincode")} testId="field-pincode" />
                    </div>

                    <div className="pt-4 border-t border-stone-200">
                        <div className="label-eyebrow text-sky-700 mb-4">Payment</div>
                        <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-3" data-testid="payment-method">
                            <div className={`flex items-center space-x-3 border p-4 transition-colors ${paymentMethod === "razorpay" ? "border-sky-700 bg-sky-50/50" : "border-stone-200"} ${gateway.enabled ? "" : "opacity-60"}`}>
                                <RadioGroupItem value="razorpay" id="razorpay" disabled={!gateway.enabled} data-testid="pay-razorpay" />
                                <Label htmlFor="razorpay" className={`flex-1 ${gateway.enabled ? "cursor-pointer" : ""}`}>
                                    <div className="font-medium flex items-center gap-2">
                                        Pay online
                                        <span className="text-[10px] uppercase tracking-widest border border-sky-700 text-sky-700 px-1.5 py-0.5">Razorpay</span>
                                        {prepaidPct > 0 && <span className="text-[10px] uppercase tracking-widest bg-emerald-600 text-white px-1.5 py-0.5" data-testid="prepaid-badge">Save {prepaidPct}%</span>}
                                        {gateway.mode === "test" && <span className="text-[10px] uppercase tracking-widest border border-amber-600 text-amber-700 px-1.5 py-0.5">Test mode</span>}
                                    </div>
                                    <div className="text-xs text-stone-500">
                                        {gateway.enabled
                                            ? "UPI, cards, netbanking & wallets — 100% secure, instant confirmation"
                                            : "Temporarily unavailable — please use Cash on Delivery"}
                                    </div>
                                </Label>
                                <Lock className="w-4 h-4 text-stone-400" />
                            </div>
                            <div className={`flex items-center space-x-3 border p-4 transition-colors ${paymentMethod === "cod" ? "border-sky-700 bg-sky-50/50" : "border-stone-200"}`}>
                                <RadioGroupItem value="cod" id="cod" data-testid="pay-cod" />
                                <Label htmlFor="cod" className="flex-1 cursor-pointer">
                                    <div className="font-medium">Cash on Delivery</div>
                                    <div className="text-xs text-stone-500">Pay when your order arrives</div>
                                </Label>
                            </div>
                        </RadioGroup>
                    </div>
                </div>

                <aside className="bg-white border border-stone-200 p-6 h-fit">
                    <div className="label-eyebrow text-stone-500 mb-4">Summary</div>
                    <div className="space-y-2 text-sm max-h-64 overflow-auto pr-2">
                        {items.map((i) => (
                            <div key={`${i.product_id}-${i.size_id}`} className="flex justify-between gap-4 border-b border-stone-100 pb-2">
                                <span className="text-stone-700 line-clamp-1">{i.product_name} <span className="text-stone-400">× {i.quantity}</span></span>
                                <span>{formatINR(i.price * i.quantity)}</span>
                            </div>
                        ))}
                    </div>

                    {/* Coupon */}
                    <div className="mt-4 pt-4 border-t border-stone-200" data-testid="coupon-section">
                        <div className="label-eyebrow text-stone-500 mb-2 inline-flex items-center gap-1"><Tag className="w-3 h-3" />Coupon</div>
                        {applied ? (
                            <div className="bg-emerald-50 border border-emerald-200 p-3 flex items-start gap-2" data-testid="coupon-applied">
                                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" />
                                <div className="flex-1">
                                    <div className="font-mono font-semibold text-emerald-900 text-sm">{applied.code}</div>
                                    <div className="text-[10px] text-emerald-700">{applied.description || `You save ${formatINR(applied.discount)}`}</div>
                                </div>
                                <button type="button" onClick={removeCoupon} className="text-emerald-700 hover:text-emerald-900" data-testid="coupon-remove">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        ) : (
                            <div className="flex gap-2">
                                <Input value={couponInput} onChange={e => setCouponInput(e.target.value.toUpperCase())} placeholder="Enter code" className="rounded-none border-stone-300 font-mono uppercase text-sm" data-testid="coupon-input" />
                                <Button type="button" onClick={() => applyCoupon()} disabled={couponBusy || !couponInput.trim()} variant="outline" className="rounded-none border-stone-400" data-testid="coupon-apply">
                                    {couponBusy ? "…" : "Apply"}
                                </Button>
                            </div>
                        )}
                    </div>

                    <div className="mt-4 pt-4 border-t border-stone-200 space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-stone-600">Subtotal</span><span>{formatINRExact(subtotal)}</span></div>
                        <div className="flex justify-between"><span className="text-stone-600">Shipping</span><span>{shipping === 0 ? "Free" : formatINRExact(shipping)}</span></div>
                        {discount > 0 && <div className="flex justify-between text-emerald-700" data-testid="discount-line"><span>Discount{applied?.code ? ` (${applied.code})` : ""}</span><span>− {formatINRExact(discount)}</span></div>}
                        {prepaidDiscount > 0 && (
                            <div className="flex justify-between text-emerald-700" data-testid="prepaid-discount-line">
                                <span>Prepaid Discount ({prepaidPct}%)</span>
                                <span>− {formatINRExact(prepaidDiscount)}</span>
                            </div>
                        )}
                        {prepaidDiscount === 0 && prepaidPct > 0 && paymentMethod === "cod" && gateway.enabled && (
                            <button
                                type="button"
                                onClick={() => setPaymentMethod("razorpay")}
                                className="w-full text-left bg-emerald-50 border border-emerald-200 px-3 py-2 text-[11px] text-emerald-900 hover:bg-emerald-100 transition-colors"
                                data-testid="prepaid-nudge"
                            >
                                Pay online and save <strong>{prepaidPct}%</strong> on this order — tap to switch.
                            </button>
                        )}
                        <div className="flex justify-between font-serif text-xl pt-2 border-t border-stone-100"><span>Total</span><span data-testid="checkout-total">{formatINRExact(total)}</span></div>
                    </div>
                    <Button type="submit" disabled={submitting} className="rounded-none w-full mt-6 py-6 bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="place-order-btn">
                        {submitting
                            ? (paymentMethod === "razorpay" ? "Opening secure payment…" : "Placing…")
                            : (paymentMethod === "razorpay" ? `Pay ${formatINRExact(total)} securely` : "Place order")}
                    </Button>
                    {paymentMethod === "razorpay" && gateway.enabled && (
                        <p className="text-[10px] text-stone-500 mt-2 text-center leading-relaxed" data-testid="razorpay-assurance">
                            You'll be redirected to Razorpay's secure window. Your order is created only after the payment succeeds.
                        </p>
                    )}
                </aside>
            </form>
        </div>
    );
}

const Field = ({ label, v, on, testId }) => (
    <div>
        <Label className="text-xs uppercase tracking-widest text-stone-500">{label}</Label>
        <Input value={v} onChange={on} className="rounded-none border-stone-300 mt-1" data-testid={testId} />
    </div>
);
